import { Link } from "react-router-dom";
import PageContainer, { PageHeader } from "@/components/PageContainer";
import { ArrowRight, Layers, Users, LineChart, FileCheck } from "lucide-react";
import { FRAMEWORKS, LEVELS_OF_CARE, NON_NEGOTIABLES } from "@/data/content";

const STATS = [
  { label: "Core frameworks", value: "9", note: "13 additional in the library" },
  { label: "Levels of care", value: "10", note: "NARR I–IV + ASAM 1.0–4.0" },
  { label: "Role playbooks", value: "8", note: "Reception → Medical" },
  { label: "Playbook pages", value: "152", note: "Print-ready master doc" },
];

export default function Overview() {
  return (
    <PageContainer testid="overview-page">
      <PageHeader
        overline="Overview · v2.0 · July 2026"
        title="A behavioral health continuum of care — from sober living to ASAM 4.0."
        subtitle="One shared operating system for every role — from the receptionist who answers the first call to the medical director who signs off on inpatient admissions. Frameworks assigned. Handoffs warm. Outcomes measured."
        actions={
          <div className="flex gap-2">
            <Link to="/book-call" className="btn btn-primary" data-testid="overview-book-cta">
              Book a call <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link to="/pricing" className="btn btn-secondary" data-testid="overview-pricing-cta">
              See pricing
            </Link>
          </div>
        }
      />

      {/* Stats */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14" data-testid="overview-stats">
        {STATS.map((s) => (
          <div key={s.label} className="card p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-3xl font-semibold text-navy">{s.value}</div>
            <div className="text-sm text-ink mt-1">{s.label}</div>
            <div className="text-xs text-ink-muted mt-0.5">{s.note}</div>
          </div>
        ))}
      </section>

      {/* Purpose */}
      <section className="grid lg:grid-cols-3 gap-6 mb-14" data-testid="overview-purpose">
        <div className="lg:col-span-2 card p-7">
          <div className="overline mb-3">Purpose</div>
          <h2 className="text-navy text-2xl font-semibold mb-4 tracking-tightest">
            Eliminate the fragmentation between levels of care.
          </h2>
          <p className="text-ink leading-relaxed mb-4">
            Most behavioral health organizations operate as a collection of programs rather than a continuum. Each
            level of care runs its own workflows, uses its own language, measures its own outcomes, and hands
            patients off with a discharge summary and a hope.
          </p>
          <p className="text-ink leading-relaxed">
            The Catalyst Continuum assigns nine core evidence-based frameworks specific jobs in the patient's
            journey and specific jobs in each staff member's daily work. A patient in Level 3.5 residential care
            experiences the same identity-supportive language from the BHT running morning group as they do from
            the case manager coordinating aftercare — and both are looking at the same PHQ-9, GAD-7, and ORS/SRS
            trend the therapist reviewed that morning.
          </p>
        </div>
        <div className="card p-7 bg-teal-light/40 border-teal/30">
          <div className="overline mb-3">Framework anchors</div>
          <ul className="space-y-2 text-sm text-navy">
            <li>· ASAM Criteria 4th Edition (2023)</li>
            <li>· NARR Recovery Residence Standards</li>
            <li>· SIMOR</li>
            <li>· 4E Cognition</li>
            <li>· CHIME</li>
            <li>· ROSC</li>
            <li>· Feedback-Informed Treatment (FIT)</li>
            <li>· Measurement-Based Care (MBC)</li>
            <li>· Deliberate Practice</li>
          </ul>
          <div className="rule my-5 bg-teal/30" />
          <div className="text-xs text-navy">
            Regulatory spine: national standards with Arizona (AHCCCS / ADHS Article 7) and Colorado (BHA)
            appendices.
          </div>
        </div>
      </section>

      {/* How they fit */}
      <section className="mb-14 card p-7" data-testid="overview-fit">
        <div className="overline mb-3">The framework stack in one sentence</div>
        <p className="text-ink text-lg leading-relaxed">
          <span className="text-navy font-medium">ASAM</span> decides where the patient belongs;{" "}
          <span className="text-navy font-medium">NARR</span> governs how the sober-living rung looks;{" "}
          <span className="text-navy font-medium">CHIME</span> describes what recovery is;{" "}
          <span className="text-navy font-medium">SIMOR</span> explains how it happens socially;{" "}
          <span className="text-navy font-medium">4E</span> extends where it happens;{" "}
          <span className="text-navy font-medium">ROSC</span> organizes what surrounds it;{" "}
          <span className="text-navy font-medium">MBC and FIT</span> tell us whether it is working; and{" "}
          <span className="text-navy font-medium">deliberate practice</span> ensures our clinicians keep getting
          better at delivering it.
        </p>
      </section>

      {/* Framework quick grid */}
      <section className="mb-16" data-testid="overview-frameworks">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="overline mb-2">The framework stack</div>
            <h2 className="text-navy text-2xl font-semibold tracking-tightest">Nine frameworks, each with a job.</h2>
          </div>
          <Link to="/frameworks" className="btn btn-ghost" data-testid="overview-frameworks-link">
            View all <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FRAMEWORKS.map((f) => (
            <div key={f.id} className="card card-hover p-5" data-testid={`fw-${f.id}`}>
              <div className="overline text-teal mb-1">{f.origin.split(",")[0]}</div>
              <div className="text-navy font-semibold text-lg mb-2">{f.name}</div>
              <div className="text-sm text-ink leading-relaxed">{f.job}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Levels quick view */}
      <section className="mb-16" data-testid="overview-levels">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="overline mb-2">Continuum at a glance</div>
            <h2 className="text-navy text-2xl font-semibold tracking-tightest">
              Ten levels of care, one operating system.
            </h2>
          </div>
          <Link to="/levels" className="btn btn-ghost" data-testid="overview-levels-link">
            View all <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </div>
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream">
              <tr className="text-left text-ink-muted">
                <th className="p-4 font-medium">Segment</th>
                <th className="p-4 font-medium">Levels</th>
                <th className="p-4 font-medium hidden md:table-cell">Setting</th>
                <th className="p-4 font-medium hidden lg:table-cell">Typical LOS</th>
              </tr>
            </thead>
            <tbody>
              {LEVELS_OF_CARE.map((l, i) => (
                <tr key={l.id} className={i > 0 ? "border-t border-line-soft" : ""}>
                  <td className="p-4 text-navy font-medium">{l.segment}</td>
                  <td className="p-4 text-ink">{l.levels}</td>
                  <td className="p-4 text-ink-muted hidden md:table-cell">{l.setting}</td>
                  <td className="p-4 text-ink-muted hidden lg:table-cell">{l.los}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Non-negotiables */}
      <section className="mb-14" data-testid="overview-nonneg">
        <div className="overline mb-2">Non-negotiables</div>
        <h2 className="text-navy text-2xl font-semibold tracking-tightest mb-5">
          Regardless of level, role, or state, these are always true.
        </h2>
        <div className="grid md:grid-cols-2 gap-3">
          {NON_NEGOTIABLES.map((n, i) => (
            <div key={i} className="card p-4 flex gap-3">
              <div className="text-xs font-semibold text-teal min-w-[24px] pt-0.5">{String(i + 1).padStart(2, "0")}</div>
              <div className="text-sm text-ink leading-relaxed">{n}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="rounded-xl border border-navy shadow-card p-8 lg:p-10 bg-navy text-cream" data-testid="overview-cta">
        <div className="grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <div className="overline text-teal-light mb-2">Next step</div>
            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tightest text-cream">
              Bring the continuum into your organization.
            </h2>
            <p className="mt-3 text-cream leading-relaxed" data-testid="overview-cta-copy">
              A 20-minute discovery call maps your current state against the continuum and identifies the right
              tier. No pitch — a working conversation.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/book-call" className="btn btn-primary" data-testid="overview-cta-book">
              Book a discovery call <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link
              to="/reference-card"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium border border-cream/40 bg-transparent text-cream hover:bg-cream/10 hover:border-cream transition-colors"
              data-testid="overview-cta-ref"
            >
              Get the reference card
            </Link>
          </div>
        </div>
      </section>
    </PageContainer>
  );
}
